import {omit} from 'lodash';

import {
  NOTIFICATION_SCOPE_ALL,
  NOTIFICATION_TYPE_HF_FORECAST_THRESHOLD_START,
  NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST,
} from 'src/lib/constants';

import {getAllPages, makeApiRequest} from './api';
import {NotificationScope, Project, Site} from './project';
import {AllColumns, Source} from './series';

export type NotificationType =
  | typeof NOTIFICATION_TYPE_HF_NEW_SHORT_TERM_FORECAST
  | typeof NOTIFICATION_TYPE_HF_FORECAST_THRESHOLD_START;

export type NewForecastNotificationProperties = {
  hfProjectId: Project['id'];
  siteId: Site['id'];
  type: NotificationType;
  '0Z'?: boolean;
  '12Z'?: boolean;
  scope: NotificationScope;
};

export type ForecastThresholdNotificationProperties = {
  hfProjectId: Project['id'];
  siteId: Site['id'];
  type: NotificationType;
  source: Source;
  column: AllColumns;
  direction: 'above' | 'below';
  threshold: number;
  isEnabled: boolean;
  hydroforecast_alert_config_id: number;
  scope: NotificationScope;
};

export type ApiNotificationPreference = {
  id: number;
  projectId: Project['id'];
  type: NotificationType;
  scope: NotificationScope;
  properties: any;
  ccEmails?: string[];
};

export interface ApiNewForecastNotificationPreference extends ApiNotificationPreference {
  properties: NewForecastNotificationProperties;
}
export interface ApiForecastThresholdNotificationPreference extends ApiNotificationPreference {
  properties: ForecastThresholdNotificationProperties;
}

export async function listNotificationPreferences(
  type: ApiNotificationPreference['type']
): Promise<ApiNotificationPreference[]> {
  const {data: notificationPreferences} = await getAllPages(
    (params) =>
      makeApiRequest<ApiNotificationPreference[]>('/prefs/notifications', {
        ...params,
        notificationType: type,
      }),
    {},
    2
  );

  return notificationPreferences;
}

export async function fetchNotificationPreference(
  id: ApiNotificationPreference['id']
): Promise<ApiNotificationPreference> {
  const {data: notificationPreference} = await makeApiRequest<ApiNotificationPreference>(
    `prefs/notifications/${id}`
  );
  return notificationPreference;
}

export type CreateNotificationPreference = Omit<ApiNotificationPreference, 'id'>;
export async function createNotificationPref(
  data: CreateNotificationPreference
): Promise<ApiNotificationPreference> {
  const response = await makeApiRequest<ApiNotificationPreference>(`prefs/notifications`, null, {
    method: 'POST',
    body: JSON.stringify({
      ...omit(data, ['projectId', 'type']), // projectId is omitted because HF Projects aren't in the database.
      notificationType: data.type,
    }),
  });
  return response.data;
}

export async function updateNotificationPref(
  data: ApiNotificationPreference
): Promise<ApiNotificationPreference> {
  const response = await makeApiRequest<ApiNotificationPreference>(
    `prefs/notifications/${data.id}`,
    null,
    {
      method: 'PUT',
      body: JSON.stringify({
        ...omit(data, ['projectId', 'type']),
      }),
    }
  );
  return response.data;
}

export async function deleteNotificationPref(
  id: ApiNotificationPreference['id']
): Promise<ApiNotificationPreference> {
  const response = await makeApiRequest<ApiNotificationPreference>(
    `prefs/notifications/${id}`,
    null,
    {
      method: 'DELETE',
    }
  );
  return response.data;
}
